import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, ShieldCheck, KeyRound, UserPlus } from "lucide-react";
import { Input, Select } from "./fields";
import { listUsers, createUser, updateUser, deleteUser, changePasswordApi } from "@/lib/api";

const ROLES = [
  { value: "owner", label: "Propriétaire (tout + gestion des comptes)" },
  { value: "admin", label: "Administrateur (éditer + publier)" },
  { value: "editor", label: "Éditeur (brouillon uniquement)" },
];
const roleLabel = (r) => ({ owner: "Propriétaire", admin: "Administrateur", editor: "Éditeur" }[r] || r);

export const AdminUsers = ({ currentUser }) => {
  const [users, setUsers] = useState(null);
  const [creating, setCreating] = useState({ email: "", name: "", password: "", role: "editor" });
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  const load = async () => { try { setUsers(await listUsers()); } catch { toast.error("Impossible de charger les comptes."); } };
  useEffect(() => { load(); }, []);

  const doCreate = async () => {
    if (!creating.email || !creating.name || creating.password.length < 6) { toast.error("Nom, email et mot de passe (6+ caractères) requis."); return; }
    setBusy(true);
    try { await createUser(creating); toast.success("Compte créé."); setCreating({ email: "", name: "", password: "", role: "editor" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec de la création."); }
    finally { setBusy(false); }
  };
  const doUpdate = async (id, patch) => {
    try { await updateUser(id, patch); toast.success("Compte mis à jour."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec de la mise à jour."); }
  };
  const doDelete = async (id, name) => {
    if (!window.confirm(`Supprimer le compte « ${name} » ? Cette action est définitive.`)) return;
    try { await deleteUser(id); toast.success("Compte supprimé."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec de la suppression."); }
  };
  const doChangePw = async () => {
    if (pw.new_password.length < 6) { toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères."); return; }
    try { await changePasswordApi(pw); toast.success("Mot de passe modifié."); setPw({ current_password: "", new_password: "" }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec du changement de mot de passe."); }
  };

  if (!users) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-electric" /></div>;

  return (
    <div className="space-y-8" data-testid="admin-users-editor">
      <div className="rounded-lg border border-electric/20 bg-[#0a0a0a] p-4 text-sm text-secondary2">
        Gérez les comptes administrateurs et leurs niveaux d'autorisation. <span className="text-white">Propriétaire</span> : accès total + gestion des comptes. <span className="text-white">Administrateur</span> : édition + publication. <span className="text-white">Éditeur</span> : brouillon uniquement (ne peut pas publier).
      </div>

      {/* Existing users */}
      <div className="space-y-3">
        <span className="text-[11px] tracking-[0.25em] uppercase text-electric">Comptes existants</span>
        {users.map((u) => (
          <div key={u.id} data-testid={`admin-user-${u.email}`} className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_180px_auto] gap-3 items-end">
              <Input label="Nom" value={u.name} onChange={(v) => setUsers((arr) => arr.map((x) => x.id === u.id ? { ...x, name: v } : x))} />
              <Input label="Email" value={u.email} onChange={(v) => setUsers((arr) => arr.map((x) => x.id === u.id ? { ...x, email: v } : x))} />
              <Select label="Rôle" value={u.role} onChange={(v) => doUpdate(u.id, { role: v })} options={ROLES} />
              <div className="flex items-center gap-3 pb-1">
                <button onClick={() => doUpdate(u.id, { name: u.name, email: u.email })} className="text-xs text-electric hover:text-white inline-flex items-center gap-1"><ShieldCheck size={14} /> Enregistrer</button>
                {u.id !== currentUser?.id && <button onClick={() => doDelete(u.id, u.name)} data-testid={`admin-user-delete-${u.email}`} className="text-red-400/70 hover:text-red-400"><Trash2 size={15} /></button>}
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <span className="text-[10px] tracking-[0.15em] uppercase text-secondary2 border border-white/10 rounded-full px-3 py-1">{roleLabel(u.role)}{u.id === currentUser?.id ? " · vous" : ""}</span>
              <ResetPw onReset={(np) => doUpdate(u.id, { password: np })} />
            </div>
          </div>
        ))}
      </div>

      {/* Create user */}
      <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4 space-y-3">
        <span className="text-[11px] tracking-[0.25em] uppercase text-electric inline-flex items-center gap-2"><UserPlus size={14} /> Nouveau compte</span>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="Nom" value={creating.name} onChange={(v) => setCreating((c) => ({ ...c, name: v }))} />
          <Input label="Email" value={creating.email} onChange={(v) => setCreating((c) => ({ ...c, email: v }))} />
          <Input label="Mot de passe (6+ caractères)" value={creating.password} onChange={(v) => setCreating((c) => ({ ...c, password: v }))} />
          <Select label="Rôle" value={creating.role} onChange={(v) => setCreating((c) => ({ ...c, role: v }))} options={ROLES} />
        </div>
        <button onClick={doCreate} disabled={busy} data-testid="admin-create-user-btn" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-electric text-black text-xs tracking-[0.1em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Créer le compte
        </button>
      </div>

      {/* Change my password */}
      <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4 space-y-3">
        <span className="text-[11px] tracking-[0.25em] uppercase text-electric inline-flex items-center gap-2"><KeyRound size={14} /> Mon mot de passe</span>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="Mot de passe actuel" value={pw.current_password} onChange={(v) => setPw((p) => ({ ...p, current_password: v }))} />
          <Input label="Nouveau mot de passe" value={pw.new_password} onChange={(v) => setPw((p) => ({ ...p, new_password: v }))} />
        </div>
        <button onClick={doChangePw} data-testid="admin-change-pw-btn" className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-white/15 text-white text-xs tracking-[0.1em] uppercase hover:border-electric hover:text-electric transition-colors">
          <KeyRound size={14} /> Changer mon mot de passe
        </button>
      </div>
    </div>
  );
};

const ResetPw = ({ onReset }) => {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  if (!open) return <button onClick={() => setOpen(true)} className="text-[11px] text-secondary2 hover:text-electric inline-flex items-center gap-1"><KeyRound size={12} /> Réinitialiser le mot de passe</button>;
  return (
    <div className="flex items-center gap-2">
      <input value={val} onChange={(e) => setVal(e.target.value)} placeholder="Nouveau mot de passe" className="bg-[#0e0e0e] border border-white/10 rounded px-3 py-1.5 text-white text-xs focus:border-electric outline-none" />
      <button onClick={() => { if (val.length < 6) { toast.error("6 caractères minimum."); return; } onReset(val); setVal(""); setOpen(false); }} className="text-xs text-electric hover:text-white">OK</button>
      <button onClick={() => { setVal(""); setOpen(false); }} className="text-xs text-secondary2">Annuler</button>
    </div>
  );
};
